import React, { useState, useEffect } from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

interface SessionTimerProps {
  compact?: boolean;
}

const SessionTimer: React.FC<SessionTimerProps> = ({ compact = false }) => {
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);

  useEffect(() => {
    // Calculate expiry time from token storage time
    const token = localStorage.getItem("authToken");
    const authUser = localStorage.getItem("authUser");

    if (!token || !authUser) {
      return;
    }

    // Check if we have a stored login time
    let loginTime = localStorage.getItem("loginTime");

    if (!loginTime) {
      // If no login time stored, set it to now (for existing sessions)
      loginTime = new Date().toISOString();
      localStorage.setItem("loginTime", loginTime);
    }

    const loginDate = new Date(loginTime);
    const expiryDate = new Date(loginDate.getTime() + 24 * 60 * 60 * 1000); // 24 hours
    setExpiryTime(expiryDate);

    const updateTimer = () => {
      const now = new Date();
      const diff = expiryDate.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining("Expired");
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      // Warning when less than 1 hour
      setIsWarning(hours === 0 && minutes < 60);
      // Critical when less than 10 minutes
      setIsCritical(hours === 0 && minutes < 10);

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  if (!timeRemaining) {
    return null;
  }

  const getColor = () => {
    if (isCritical) return "error";
    if (isWarning) return "warning";
    return "success";
  };

  const getTooltipText = () => {
    if (!expiryTime) return "";
    return `Session expires at ${expiryTime.toLocaleTimeString("en-GB")}`;
  };

  if (compact) {
    return (
      <Tooltip title={getTooltipText()} arrow>
        <Chip
          icon={isCritical ? <WarningIcon /> : <AccessTimeIcon />}
          label={timeRemaining}
          color={getColor()}
          size="small"
          variant={isWarning ? "filled" : "outlined"}
          sx={{
            fontWeight: isWarning ? "bold" : "normal",
            animation: isCritical ? "pulse 1s infinite" : "none",
            ...(getColor() === "success" && {
              borderColor: "#4caf50",
              color: "#fff",
              bgcolor: "#4caf50",
              "& .MuiChip-icon": {
                color: "#fff",
              },
            }),
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.7 },
            },
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={getTooltipText()} arrow>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: isCritical
            ? "error.light"
            : isWarning
            ? "warning.light"
            : "success.light",
          color: isCritical
            ? "error.dark"
            : isWarning
            ? "warning.dark"
            : "success.dark",
          animation: isCritical ? "pulse 1s infinite" : "none",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.7 },
          },
        }}
      >
        {isCritical ? (
          <WarningIcon fontSize="small" />
        ) : (
          <AccessTimeIcon fontSize="small" />
        )}
        <Box>
          <Typography
            variant="caption"
            display="block"
            sx={{ lineHeight: 1.2, fontWeight: 600 }}
          >
            Session
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", lineHeight: 1.2 }}
          >
            {timeRemaining}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default SessionTimer;
